"use client";

/**
 * Team Renderer
 * Renders a team member grid with label filter tabs on the canvas
 */

import type React from "react";
import { useMemo, useState } from "react";
import { Linkedin, Twitter, Instagram, Facebook, Youtube, Music, ExternalLink, User, Mail, Phone } from "lucide-react";
import {
  LANDING_IMAGE_RENDER_QUALITY,
  MAX_TEAM_PREVIEW_COUNT,
  TEAM_IMAGE_RENDER_WIDTH,
} from "../../../_logic/constants";
import { transformStorageImageUrl } from "../../../_logic/image-cdn";
import { normalizeUrl } from "../../../_logic/link-utils";
import type { TeamComponent, TeamMember, TeamMemberLinkType } from "../../../_types";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { useCanvasFieldClick } from "../../../_hooks/useCanvasFieldClick";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { InlineTipTapEdit } from "./_shared/InlineTipTapEdit";
import { SectionContainer } from "./_shared/SectionContainer";
import { PROSE_SECTION_TEXT_VARS } from "./_shared/prose-section-text";

interface TeamRendererProps {
  component: TeamComponent;
}

/** Returns the appropriate icon for a link type */
function LinkIcon({ linkType }: { linkType: TeamMemberLinkType }): React.ReactElement {
  switch (linkType) {
    case "linkedin":
      return <Linkedin className="h-4 w-4" />;
    case "twitter":
      return <Twitter className="h-4 w-4" />;
    case "instagram":
      return <Instagram className="h-4 w-4" />;
    case "facebook":
      return <Facebook className="h-4 w-4" />;
    case "youtube":
      return <Youtube className="h-4 w-4" />;
    case "tiktok":
      return <Music className="h-4 w-4" />;
    default:
      return <ExternalLink className="h-4 w-4" />;
  }
}

/** Renders a team member grid with label filtering tabs */
export function TeamRenderer({ component }: TeamRendererProps): React.ReactElement {
  const { title, description, members = [], labels = [] } = component;
  const { primaryColor } = useBuilderContext();
  const { isPublic } = usePublicViewContext();

  // States
  const [activeLabelId, setActiveLabelId] = useState<string | null>(null);

  // Derived values
  const filteredMembers = useMemo(() => {
    if (!activeLabelId) return members;
    return members.filter((m) => m.labelIds.includes(activeLabelId));
  }, [members, activeLabelId]);

  // Public view shows the full roster; the builder canvas keeps a capped preview for scannability.
  const previewMembers = isPublic ? filteredMembers : filteredMembers.slice(0, MAX_TEAM_PREVIEW_COUNT);
  const overflowCount = isPublic ? 0 : filteredMembers.length - MAX_TEAM_PREVIEW_COUNT;

  // Render
  return (
    <SectionContainer
      maxWidth="4xl"
      background={component.background}
      title={title || ""}
      componentId={component.id}
      titleFieldId="team-title"
    >
      <InlineTipTapEdit
        content={description || ""}
        className={`prose prose-base prose-gray max-w-none prose-a:text-[var(--lp-accent)] prose-a:underline ${PROSE_SECTION_TEXT_VARS}`}
        componentId={component.id}
        fieldId="team-description"
        field="description"
        placeholder="Click to add description..."
      />
      {members.length === 0 ? null : (
        <div className="flex flex-col gap-6">
          {/* Label filter tabs. Inactive tabs sit straight on the section background, so their
              text follows --lp-text and the hover pill follows --lp-surface-hover — both fall back
              to the original gray/gray-100 pair, so a page with no override keeps exactly the
              affordance it had. */}
          {labels.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-1">
              <button
                type="button"
                onClick={() => setActiveLabelId(null)}
                className={`shrink-0 touch-manipulation rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeLabelId === null
                    ? ""
                    : "text-[color:var(--lp-text,#4b5563)] hover:bg-[color:var(--lp-surface-hover,#f3f4f6)]"
                }`}
                style={
                  activeLabelId === null ? { backgroundColor: `${primaryColor}1a`, color: primaryColor } : undefined
                }
              >
                All
              </button>
              {labels.map((label) => {
                const labelColor = label.color || "#6366f1";
                const isActive = activeLabelId === label.id;
                return (
                  <button
                    type="button"
                    key={label.id}
                    onClick={() => setActiveLabelId(label.id)}
                    className={`shrink-0 touch-manipulation rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                      isActive
                        ? ""
                        : "text-[color:var(--lp-text,#4b5563)] hover:bg-[color:var(--lp-surface-hover,#f3f4f6)]"
                    }`}
                    style={isActive ? { backgroundColor: `${labelColor}20`, color: labelColor } : undefined}
                  >
                    {label.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* Member grid */}
          {/* The empty-state line is secondary text — gray-400 originally, so it belongs on the
              muted tier like the member job title, not on full-strength `--lp-text`. */}
          {filteredMembers.length === 0 ? (
            <p className="text-center text-sm font-medium text-[color:var(--lp-text-muted,#9ca3af)]">
              No members with this label.
            </p>
          ) : (
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-8">
              {previewMembers.map((member) => (
                <TeamMemberCard key={member.id} member={member} componentId={component.id} isPublic={isPublic} />
              ))}
            </div>
          )}

          {/* Same tier as the job title (gray-500 originally) — full strength here made the
              overflow count louder than the names it's counting under an override. */}
          {overflowCount > 0 && (
            <p className="text-center text-sm font-medium text-[color:var(--lp-text-muted,#6b7280)]">
              +{overflowCount} more {overflowCount === 1 ? "member" : "members"}
            </p>
          )}
        </div>
      )}
    </SectionContainer>
  );
}

/** Individual team member card — compact, no border */
function TeamMemberCard({
  member,
  componentId,
  isPublic,
}: {
  member: TeamMember;
  componentId: string;
  isPublic: boolean;
}): React.ReactElement {
  const { primaryColor } = useBuilderContext();
  const fieldClick = useCanvasFieldClick(componentId);
  const hasContactIcons = member.link || member.email || member.phone;
  const widthClass = isPublic
    ? "sm:w-[calc(33.333%-1rem)] md:w-[calc(25%-1.125rem)]"
    : "@sm:w-[calc(33.333%-1rem)] @md:w-[calc(25%-1.125rem)]";
  const imageSrc = useMemo(
    () =>
      member.imageUrl
        ? transformStorageImageUrl(member.imageUrl, {
            width: TEAM_IMAGE_RENDER_WIDTH,
            quality: LANDING_IMAGE_RENDER_QUALITY,
          })
        : "",
    [member.imageUrl]
  );

  return (
    <div
      className={`flex w-[calc(50%-0.75rem)] flex-col items-center gap-2 py-3 ${widthClass}${!isPublic ? " cursor-pointer transition-opacity hover:opacity-80" : ""}`}
      onClick={!isPublic ? fieldClick(`team-item:${member.id}`) : undefined}
    >
      {/* Avatar */}
      {member.imageUrl ? (
        <img
          src={imageSrc}
          alt={member.name || "Team member"}
          loading="lazy"
          decoding="async"
          className="aspect-square w-full max-w-[14rem] rounded-full object-cover"
        />
      ) : (
        <div
          className="flex aspect-square w-full max-w-[14rem] items-center justify-center rounded-full"
          style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}88)` }}
        >
          {member.name?.trim() ? (
            <span className="text-4xl font-semibold text-white">{member.name.trim()[0].toUpperCase()}</span>
          ) : (
            <User className="h-20 w-20 text-white/80" />
          )}
        </div>
      )}

      {/* Info */}
      <div className="flex flex-col items-center gap-0.5 text-center">
        {/*
          Deliberately the raw `primaryColor`, not `--lp-accent`. This started as
          `style={{ color: primaryColor }}` and moving it onto the corrected accent changed it on
          every live page, override or not: `--lp-accent` is published unconditionally, and the
          correction is not a no-op on a light background — amber `#f59e0b` resolves to `#a54e00`
          and a pastel `#C6D2F7` to `#626e93`. Correcting a name that sits on a coloured section is
          worth doing, but it belongs in the contrast follow-up PR alongside the filter chips and
          the schedule carousel, not in a change that repaints pages nobody edited.
        */}
        <p className="text-sm font-medium" style={{ color: primaryColor }}>
          {member.name || "Unnamed"}
        </p>
        {member.title && (
          <p className="text-xs font-medium text-[color:var(--lp-text-muted,#6b7280)]">{member.title}</p>
        )}
      </div>

      {/* Contact icons */}
      {hasContactIcons && (
        <div className="flex items-center gap-2">
          {member.link &&
            (isPublic ? (
              <a
                href={normalizeUrl(member.link)}
                target="_blank"
                rel="noopener noreferrer"
                className="touch-manipulation text-[color:var(--lp-text-muted,#9ca3af)] transition-colors hover:text-[color:var(--lp-text,#4b5563)]"
                aria-label={`${member.name}'s ${member.linkType} profile`}
              >
                <LinkIcon linkType={member.linkType} />
              </a>
            ) : (
              <span className="text-[color:var(--lp-text-muted,#9ca3af)]">
                <LinkIcon linkType={member.linkType} />
              </span>
            ))}
          {member.email &&
            (isPublic ? (
              <a
                href={`mailto:${member.email}`}
                className="touch-manipulation text-[color:var(--lp-text-muted,#9ca3af)] transition-colors hover:text-[color:var(--lp-text,#4b5563)]"
                aria-label={`Email ${member.name}`}
              >
                <Mail className="h-4 w-4" />
              </a>
            ) : (
              <span className="text-[color:var(--lp-text-muted,#9ca3af)]">
                <Mail className="h-4 w-4" />
              </span>
            ))}
          {member.phone &&
            (isPublic ? (
              <a
                href={`tel:${member.phone}`}
                className="flex touch-manipulation items-center gap-1 text-xs text-[color:var(--lp-text-muted,#9ca3af)] transition-colors hover:text-[color:var(--lp-text,#4b5563)]"
                aria-label={`Call ${member.name}`}
              >
                <Phone className="h-4 w-4 shrink-0" />
                {member.phone}
              </a>
            ) : (
              <span className="flex items-center gap-1 text-xs text-[color:var(--lp-text-muted,#9ca3af)]">
                <Phone className="h-4 w-4 shrink-0" />
                {member.phone}
              </span>
            ))}
        </div>
      )}
    </div>
  );
}
